import QrCode2Icon from '@mui/icons-material/QrCode2';
import { Grid } from '@mui/material';
import type { MRT_ColumnDef } from 'material-react-table';
import { useCallback, useMemo, useState } from 'react';

import { ViewItemSeriesModal } from '@/erp/tecnico/materiales/shared';
import {
  TipoRubroChoiceEnum,
  emptyCellOneLevel,
  formatCurrencyCell,
  formatQuantityCell,
} from '@/shared';
import { SampleTable, SingleIconButton } from '@/shared/components/ui';
import { ScrollableDialogProps } from '@/shared/components/ui/Dialogs';
import { Contrato, Rubro, RubroProductDetail } from '@/shared/interfaces';
import { MaterialesKey, useMaterialesUtilizadosStore } from '@/store/erp';
import { useColumnsRubrosCliente } from '../../../hooks';
import {
  TotalAmountRubloLireCard,
  TotalAmountRubroServicioCard,
} from '../../form/rubros';
import ClienteRubroCardHeader from './ClienteRubroCardHeader';

export type RubroClienteInfoModalProps = {
  open: boolean;
  onClose: () => void;
  rubro: Rubro;
  contract: Contrato;
};

export type RubroProductDetailTableType = RubroProductDetail & {
  contieneSeries: boolean;
  savedSeries: string[];
};
export type RubroServiceDetailTableType = RubroProductDetail & {};

const RubroClienteInfoModal: React.FC<RubroClienteInfoModalProps> = ({
  contract,
  onClose,
  open,
  rubro,
}) => {
  const rubroType = rubro?.tipo_rubro;

  ///* local state -------------------
  const [openSeriesModal, setOpenSeriesModal] = useState<boolean>(false);

  ///* global state -------------------
  const setActiveItemRow = useMaterialesUtilizadosStore(
    s => s.setActiveItemRow
  );
  const updateSelectedItemQuantity = useMaterialesUtilizadosStore(
    s => s.updateSelectedItemQuantity
  );

  ///* handlers -------------------
  const onChangeSeries = useCallback(
    (value: string[], item: RubroProductDetailTableType) => {
      updateSelectedItemQuantity(
        { ...item, seriesAll: value },
        MaterialesKey.selectedItems,
        'id'
      );
    },
    [updateSelectedItemQuantity]
  );

  ///* columns -------------------
  const { columnsClienteRubrosView } = useColumnsRubrosCliente({ contract });

  const columnsClienteRubroLibreViewSeries = useMemo<
    MRT_ColumnDef<RubroProductDetailTableType>[]
  >(
    () => [
      ...(columnsClienteRubrosView as any),
      {
        accessorKey: 'selected_series',
        header: 'Series Seleccionadas',
        Cell: ({ row }) => {
          return !row.original?.contieneSeries
            ? 'SIN SERIES'
            : row.original?.savedSeries?.length
              ? row.original?.savedSeries?.length
              : 0;
        },
      },
      {
        accessorKey: 'series',
        header: 'Series Disponibles',
        Cell: ({ row }) => {
          const hasSeries = row.original?.contieneSeries;

          return hasSeries ? (
            <>
              <SingleIconButton
                startIcon={<QrCode2Icon />}
                color="info"
                label="Ver Series"
                onClick={() => {
                  setOpenSeriesModal(true);

                  const allSeries: string[] = row.original?.series || [];
                  setActiveItemRow({
                    ...row.original,
                    allSeries,
                  });

                  // set series to product in table
                  onChangeSeries(allSeries, row.original);
                }}
              />
            </>
          ) : (
            <>SIN SERIES</>
          );
        },
      },
    ],
    []
  );

  const columnsClienteRubroServicioView = useMemo<
    MRT_ColumnDef<RubroServiceDetailTableType>[]
  >(
    () => [
      {
        accessorKey: 'descripcion',
        header: 'Descripción',
        size: 300,
        Cell: ({ row }) => emptyCellOneLevel(row, 'descripcion'),
      },
      {
        accessorKey: 'precio_1',
        header: 'Precio Unitario',
        size: 150,
        Cell: ({ row }) => formatCurrencyCell(row, 'precio_1'),
      },
      {
        accessorKey: 'cantidad',
        header: 'Cantidad',
        size: 150,
        Cell: ({ row }) => formatQuantityCell(row, 'cantidad'),
      },
      {
        accessorKey: 'totalLine',
        header: 'Total',
        size: 150,
        Cell: ({ row }) => formatCurrencyCell(row, 'totalLine'),
      },
    ],
    []
  );

  if (!rubro) return null;

  return (
    <ScrollableDialogProps
      open={open}
      title={`Rubro: ${rubroType || ''}`}
      minWidth="66%"
      cancelTextBtn="Cerrar"
      onCloseIcon={onClose}
      contentNode={
        <>
          {rubroType === TipoRubroChoiceEnum.SERVICIO ? (
            <>
              {/* ================== rubro header ================== */}
              <Grid item xs={12}>
                <ClienteRubroCardHeader rubro={rubro} contract={contract} />
              </Grid>

              {/* ================== rubro details ================== */}
              <Grid item xs={12}>
                <SampleTable<RubroServiceDetailTableType>
                  data={ rubro?.detalle?.length! > 0 ? rubro?.detalle! :[]}
                  // data={[
                  //   ...((rubro?.detalle as RubroServiceDetailTableType[]) ||
                  //     []),
                  // ]}
                  columns={columnsClienteRubroServicioView}
                />
              </Grid>

              {/* ================== total ================== */}
              <TotalAmountRubroServicioCard
                calcSubTotal={() => rubro?.valor_base || '0.00'}
                calcTotal={() => rubro?.valor_total || '0.00'}
                calcTaxesButIvaLine={() => rubro?.valor_iva || '0.00'}
              />
            </>
          ) : rubroType === TipoRubroChoiceEnum.LIBRE ? (
            <Grid item container xs={12} spacing={3}>
              {/* ================== rubro header ================== */}
              <Grid item xs={12}>
                <ClienteRubroCardHeader rubro={rubro} contract={contract} />
              </Grid>

              {/* ================== rubro details ================== */}
              <Grid item xs={12}>
                <SampleTable<RubroProductDetailTableType>
                  data={
                    [
                      ...(rubro?.detalle as RubroProductDetailTableType[]).map(
                        item => ({
                          ...item,
                          contieneSeries: item?.series?.length > 0,
                          savedSeries: item?.series || [],
                        })
                      ),
                    ]
                  }
                  columns={columnsClienteRubroLibreViewSeries}
                />
              </Grid>

              {/* ================== total ================== */}
              <TotalAmountRubloLireCard
                calcSubTotal={() => rubro?.valor_base || '0.00'}
                calcTotalTaxes={() => rubro?.valor_iva || '0.00'}
                calcTotal={() => rubro?.valor_total || '0.00'}
              />
            </Grid>
          ) : (
            <>NOOO</>
          )}

          {/* ------ Product Series Modal ------ */}
          <ViewItemSeriesModal
            open={openSeriesModal}
            setOpen={setOpenSeriesModal}
          />
        </>
      }
    />
  );
};

export default RubroClienteInfoModal;
